// Per-student practice library. Teacher/center admin's assignment cockpit.
// Layout: sticky header with student name + section tabs → filter chip strip
// → grid of item cards (checkbox + title + status). A floating "Assignment (N)"
// button at bottom-right opens the AssignmentBuilder drawer with the picked
// items. This replaces the previous modal-only picker so the teacher has real
// working space, matching the FrenchPrep muscle memory.
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, ChevronRight, ListChecks, Loader2 } from 'lucide-react';

import { api } from '@/lib/api';
import AppShell from '@/components/AppShell';
import AssignmentBuilder from '@/components/AssignmentBuilder';
import { cn } from '@/lib/utils';
import { prettyTask } from '@/lib/labels';

const EXAM_LABEL = { celpip: 'CELPIP', pte: 'PTE Core' };
const SECTIONS = ['listening', 'reading', 'writing', 'speaking'];
const STATUS_META = {
  completed: { label: 'Completed', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  assigned: { label: 'Assigned', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  not_started: { label: 'Not started', className: 'bg-slate-100 text-slate-600 border-slate-200' },
};

export default function StudentLibraryPage() {
  const { user_id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [items, setItems] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // filter state
  const [exam, setExam] = useState(null);
  const [section, setSection] = useState('listening');
  const [statusFilter, setStatusFilter] = useState('all');
  const [taskFilter, setTaskFilter] = useState('all');

  // selection state
  const [picked, setPicked] = useState(() => new Map()); // set_id -> full item
  const [builderOpen, setBuilderOpen] = useState(false);

  const refresh = async () => {
    try {
      const [lib, a] = await Promise.all([
        api.orgStudentLibrary(user_id),
        api.orgStudentAssignments(user_id),
      ]);
      setStudent(lib.data.student);
      setItems(lib.data.items || []);
      setAssignments(a.data.assignments || []);
      const studentExams = (lib.data.student.exams || []);
      if (!exam && studentExams.length) setExam(studentExams[0]);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not load the library.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [user_id]);

  const examsInLib = useMemo(() => Array.from(new Set(items.map((i) => i.exam))), [items]);
  const filtered = useMemo(() => items.filter((i) => (
    (!exam || i.exam === exam)
    && i.section === section
    && (statusFilter === 'all' || i.status === statusFilter)
    && (taskFilter === 'all' || i.task_type === taskFilter)
  )), [items, exam, section, statusFilter, taskFilter]);

  const taskTypes = useMemo(() => {
    const s = new Set();
    items.forEach((i) => { if ((!exam || i.exam === exam) && i.section === section) s.add(i.task_type); });
    return Array.from(s);
  }, [items, exam, section]);

  const counts = useMemo(() => items.reduce((acc, i) => {
    acc.all = (acc.all || 0) + 1;
    acc[i.status] = (acc[i.status] || 0) + 1;
    return acc;
  }, { all: 0, completed: 0, assigned: 0, not_started: 0 }), [items]);

  const togglePick = (item) => {
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(item.set_id)) next.delete(item.set_id);
      else next.set(item.set_id, item);
      return next;
    });
  };

  const clearAll = () => setPicked(new Map());
  const removeItem = (setId) => setPicked((prev) => { const n = new Map(prev); n.delete(setId); return n; });

  const onCreated = async () => {
    setBuilderOpen(false);
    setPicked(new Map());
    await refresh();
  };

  const removeAssignment = async (id) => {
    try {
      await api.orgDeleteAssignment(id);
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not remove the assignment.');
    }
  };

  if (loading) return (
    <AppShell><div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading library…</div></AppShell>
  );
  if (error) return <AppShell><p className="text-sm text-rose-600">{error}</p></AppShell>;
  if (!student) return null;

  const pickedItems = Array.from(picked.values());

  return (
    <AppShell>
      <div className="pb-24">
        <Link to="/org" className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900" data-testid="library-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Teacher Admin
        </Link>

        <div className="mt-3 border-b border-slate-200 pb-6">
          <p className="mono-stat">Student library</p>
          <h1 className="mt-1 font-display text-3xl font-semibold text-slate-900">{student.name}</h1>
          <p className="text-sm text-slate-500">{student.email}</p>
          <div className="mt-4 flex flex-wrap gap-6 text-xs text-slate-500">
            <div><span className="mono-stat block">Completed</span>{counts.completed}</div>
            <div><span className="mono-stat block">Assigned</span>{counts.assigned}</div>
            <div><span className="mono-stat block">Not started</span>{counts.not_started}</div>
            <div><span className="mono-stat block">Total items</span>{counts.all}</div>
          </div>
        </div>

        {/* Existing assignments strip */}
        {assignments.length > 0 && (
          <section className="mt-6" data-testid="library-existing-assignments">
            <p className="mono-stat">Active assignments</p>
            <ul className="mt-2 divide-y divide-slate-100 rounded border border-slate-200 bg-white">
              {assignments.map((a) => (
                <li key={a.assignment_id} className="flex items-center gap-3 px-4 py-3" data-testid={`existing-assignment-${a.assignment_id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-[15px] font-semibold text-slate-900">{a.title}</p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {a.completed_count} / {a.total_count} done · by {a.assigned_by_name}
                      {a.due_at && ` · due ${new Date(a.due_at).toLocaleDateString()}`}
                    </p>
                  </div>
                  <button type="button" onClick={() => removeAssignment(a.assignment_id)}
                    data-testid={`delete-assignment-${a.assignment_id}`}
                    className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-500 hover:border-rose-300 hover:text-rose-600">
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Exam + section tabs */}
        {examsInLib.length > 1 && (
          <div className="mt-8 flex gap-2 border-b border-slate-100 pb-2">
            {examsInLib.map((e) => (
              <button key={e} type="button" onClick={() => { setExam(e); setPicked(new Map()); }}
                data-testid={`lib-exam-${e}`}
                className={cn('rounded-full px-3 py-1 text-xs font-semibold',
                  exam === e ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-100')}>
                {EXAM_LABEL[e] || e}
              </button>
            ))}
          </div>
        )}
        <div className={cn('flex border-b-2 border-slate-900', examsInLib.length > 1 ? 'mt-3' : 'mt-8')} data-testid="library-section-tabs">
          {SECTIONS.map((s) => {
            const on = section === s;
            return (
              <button key={s} type="button" onClick={() => { setSection(s); setTaskFilter('all'); }}
                data-testid={`lib-section-${s}`}
                className={cn('px-5 py-2.5 text-sm font-semibold capitalize',
                  on ? 'bg-slate-900 text-white' : 'text-slate-400 hover:text-slate-900')}>
                {s}
              </button>
            );
          })}
        </div>

        {/* Filter chips */}
        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
          {['all', 'not_started', 'assigned', 'completed'].map((st) => (
            <button key={st} type="button" onClick={() => setStatusFilter(st)}
              data-testid={`lib-filter-${st}`}
              className={cn('rounded-full border px-3 py-1 font-medium',
                statusFilter === st ? 'border-indigo-500 bg-indigo-600 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400')}>
              {st === 'all' ? 'All' : STATUS_META[st]?.label}
            </button>
          ))}
          <span className="mx-2 h-4 w-px bg-slate-200" />
          <button type="button" onClick={() => setTaskFilter('all')}
            className={cn('rounded-full border px-3 py-1 font-medium',
              taskFilter === 'all' ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400')}>
            All task types
          </button>
          {taskTypes.map((tt) => (
            <button key={tt} type="button" onClick={() => setTaskFilter(tt)}
              data-testid={`lib-task-${tt}`}
              className={cn('rounded-full border px-3 py-1 font-medium',
                taskFilter === tt ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400')}>
              {prettyTask(tt)}
            </button>
          ))}
        </div>

        {/* Item grid */}
        <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="library-grid">
          {filtered.length === 0 && (
            <p className="col-span-full py-8 text-center text-sm text-slate-500">No items match these filters.</p>
          )}
          {filtered.map((it) => {
            const chosen = picked.has(it.set_id);
            const meta = STATUS_META[it.status];
            return (
              <button
                key={it.set_id}
                type="button"
                onClick={() => togglePick(it)}
                data-testid={`lib-item-${it.set_id}`}
                aria-pressed={chosen}
                className={cn(
                  'group relative flex items-center gap-3 rounded border bg-white px-4 py-3 text-left transition-all',
                  chosen ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-slate-200 hover:border-slate-400'
                )}
              >
                <span className={cn(
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded border-2',
                  chosen ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-300 bg-white'
                )}>
                  {chosen && <Check className="h-3 w-3" />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display text-[15px] font-semibold text-slate-900">{it.title}</p>
                  <p className="mt-0.5 text-xs text-slate-500">{prettyTask(it.task_type)} · {EXAM_LABEL[it.exam] || it.exam}</p>
                </div>
                {meta && (
                  <span className={cn('shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium', meta.className)} data-testid={`status-${it.set_id}`}>
                    {meta.label}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Floating "Assignment (N)" trigger */}
      {picked.size > 0 && !builderOpen && (
        <button
          type="button"
          onClick={() => setBuilderOpen(true)}
          data-testid="open-builder"
          className="fixed bottom-6 right-6 z-30 inline-flex items-center gap-2 rounded-full bg-indigo-600 px-5 py-3 text-sm font-semibold text-white shadow-xl hover:bg-indigo-700"
        >
          <ListChecks className="h-4 w-4" /> Build assignment ({picked.size}) <ChevronRight className="h-4 w-4" />
        </button>
      )}

      {builderOpen && (
        <AssignmentBuilder
          student={student}
          items={pickedItems}
          onClose={() => setBuilderOpen(false)}
          onCreated={onCreated}
          onClear={clearAll}
          onRemoveItem={removeItem}
        />
      )}
    </AppShell>
  );
}
